import { Box, Chip, Collapse, Stack, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FileRejection } from 'react-dropzone';
import { useState } from 'react';
import {
  IEvidence,
  IEvidenceTracking,
  IFrameworkTracking,
} from './ComplianceList';
import EvidenceItem from './EvidenceItem';

interface FrameworkAccordionProps {
  frameworkTrackingList: IFrameworkTracking[];
  evidenceTrackings: IEvidenceTracking[];
  erlNumberToUpdate: string;
  savingNote: string;
  setSavingNote: (value: string) => void;
  isUploading: boolean;
  status: boolean[];
  onTrackingChange: (
    status: boolean,
    evidence: IEvidence,
    notes?: string
  ) => void;
  onNoteChange: (erlNumber: string, value: string) => void;
  onCancelNote: (erlNumber: string) => void;
  onFileSelected: (
    files: File[],
    rejected: FileRejection[],
    erlNumber: string
  ) => void;
  onExternalLinkOpen: (evidence: IEvidence) => void;
  onDeleteAttachment: (id: string, name: string) => void;
  onDownloadAttachment: (attachment: any) => void;
  getNoteValue: (erlNumber: string) => string;
  getInitialNote: (erlNumber: string) => string;
  isSelected: (evidence: IEvidence) => boolean;
  canShowEvidence: (evidence: IEvidence, status: boolean[]) => boolean;
  canShowVersion: (version: any, status: boolean[]) => boolean;
  totalEvidenceSelected: (version: any) => number;
}

const FrameworkAccordion: React.FC<FrameworkAccordionProps> = ({
  frameworkTrackingList,
  evidenceTrackings,
  erlNumberToUpdate,
  savingNote,
  setSavingNote,
  isUploading,
  status,
  onTrackingChange,
  onNoteChange,
  onCancelNote,
  onFileSelected,
  onExternalLinkOpen,
  onDeleteAttachment,
  onDownloadAttachment,
  getNoteValue,
  getInitialNote,
  isSelected,
  canShowEvidence,
  canShowVersion,
  totalEvidenceSelected,
}) => {
  const [activeFrameworkKey, setActiveFrameworkKey] = useState('');
  const [activerVersion, setActiverVersion] = useState('');
  const [expandedEvidence, setExpandedEvidence] = useState('');

  const totalFrameworkEvidences = (
    frameworkTracking: IFrameworkTracking
  ): number =>
    frameworkTracking.versions.reduce(
      (prev, current) => prev + current.evidenceList.length,
      0
    );

  const totalEvidenceFromFramework = (
    frameworkTracking: IFrameworkTracking
  ): number =>
    frameworkTracking.versions.reduce(
      (prev, current) =>
        prev +
        evidenceTrackings.filter(
          (eT) =>
            current.evidenceList
              .map((el) => el['ERL #'])
              .includes(eT.erl_number) && eT.is_completed
        ).length,
      0
    );

  const handleEvidenceToggle = (erlNumber: string): void => {
    setExpandedEvidence(expandedEvidence === erlNumber ? '' : erlNumber);
  };

  return (
    <>
      {frameworkTrackingList.map((frameworkTracking, index) => (
        <Box key={frameworkTracking.reference} className="app-box-border mb-5">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className="cursor-pointer"
            onClick={() =>
              setActiveFrameworkKey(
                activeFrameworkKey === frameworkTracking.reference
                  ? ''
                  : frameworkTracking.reference
              )
            }
          >
            <Stack direction="row" alignItems="center" className="py-2">
              <Typography className="fw-5">
                {frameworkTracking.scfFrameworkKey}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
              <Chip
                color={
                  totalEvidenceFromFramework(frameworkTracking) ===
                  totalFrameworkEvidences(frameworkTracking)
                    ? 'primary'
                    : 'default'
                }
                label={`${totalEvidenceFromFramework(
                  frameworkTracking
                )}/${totalFrameworkEvidences(frameworkTracking)}`}
                size="small"
              />
              {activeFrameworkKey === frameworkTracking.reference ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </Stack>
          </Stack>

          {activeFrameworkKey === frameworkTracking.reference && (
            <Collapse in={activeFrameworkKey === frameworkTracking.reference}>
              {frameworkTracking.versions.map((version, vIndex) => (
                <Box
                  key={`${frameworkTracking.scfFrameworkKey}_${version.version}_${index}_${vIndex}`}
                  sx={{
                    display: canShowVersion(version, status) ? 'block' : 'none',
                  }}
                  className="app-box-border mb-2 py-1"
                >
                  {/* Version Header */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    className="cursor-pointer"
                    onClick={() =>
                      setActiverVersion(
                        activerVersion === version.reference
                          ? ''
                          : version.reference
                      )
                    }
                  >
                    <Stack
                      spacing={2}
                      direction="row"
                      alignItems="center"
                      className="py-2"
                    >
                      {version.collectionType === 'DB' ? (
                        <>
                          <Chip size="small" label={version.statementNo} />
                          <Typography className="text-gray">
                            {version.statementTitle}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Chip size="small" label={version.version} />
                          <Typography className="text-gray">
                            Framework Control
                          </Typography>
                        </>
                      )}
                    </Stack>
                    <Stack direction="row" alignItems="center">
                      <Chip
                        color={
                          totalEvidenceSelected(version) ===
                          version.evidenceList.length
                            ? 'primary'
                            : 'default'
                        }
                        label={`${totalEvidenceSelected(version)}/${
                          version.evidenceList.length
                        }`}
                        size="small"
                      />
                      {activerVersion === version.reference ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </Stack>
                  </Stack>

                  {activerVersion === version.reference && (
                    <Collapse in={activerVersion === version.reference}>
                      {version.evidenceList.map((evidence, eIndex) => (
                        <EvidenceItem
                          key={`${version.version}_${evidence['ERL #']}_${index}_${eIndex}_${vIndex}`}
                          evidence={evidence}
                          version={version}
                          expandedEvidence={expandedEvidence}
                          onToggleExpand={handleEvidenceToggle}
                          erlNumberToUpdate={erlNumberToUpdate}
                          evidenceTrackings={evidenceTrackings}
                          isSelected={isSelected}
                          onTrackingChange={onTrackingChange}
                          savingNote={savingNote}
                          setSavingNote={setSavingNote}
                          getNoteValue={getNoteValue}
                          getInitialNote={getInitialNote}
                          onNoteChange={onNoteChange}
                          onCancelNote={onCancelNote}
                          isUploading={isUploading}
                          onFileSelected={onFileSelected}
                          onExternalLinkOpen={onExternalLinkOpen}
                          onDeleteAttachment={onDeleteAttachment}
                          onDownloadAttachment={onDownloadAttachment}
                          canShowEvidence={canShowEvidence}
                          status={status}
                        />
                      ))}
                    </Collapse>
                  )}
                </Box>
              ))}
            </Collapse>
          )}
        </Box>
      ))}
    </>
  );
};

export default FrameworkAccordion;
